import { useEffect, useMemo, useState } from "react";
import {
  FiSearch,
  FiFilter,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiTruck,
  FiTrendingUp,
  FiAnchor,
  FiLayers,
  FiX,
  FiAlertCircle,
  FiCheckCircle,
  FiInfo,
} from "react-icons/fi";

import AddCategoryModal from "../components/AddCategoryModal";
import AddCostPriceModal from "../components/AddCostPriceModal";

import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from "../services/categoryApi";

import {
  getCostPrices,
  createCostPrice,
  updateCostPrice,
  deleteCostPrice,
} from "../services/costPriceApi";

function Toast({ message, type = "success", onClose }) {
  if (!message) return null;

  const isError = type === "error";

  return (
    <div className="fixed right-5 top-5 z-[10000] w-[calc(100%-40px)] max-w-md">
      <div
        className={`flex items-start gap-3 rounded-2xl border bg-white p-4 shadow-2xl dark:bg-slate-900 ${
          isError
            ? "border-red-100 text-red-700 dark:border-red-900 dark:text-red-300"
            : "border-emerald-100 text-emerald-700 dark:border-emerald-900 dark:text-emerald-300"
        }`}
      >
        <div
          className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${
            isError
              ? "bg-red-50 dark:bg-red-950"
              : "bg-emerald-50 dark:bg-emerald-950"
          }`}
        >
          {isError ? <FiAlertCircle /> : <FiCheckCircle />}
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold">
            {isError ? "Action failed" : "Success"}
          </p>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            {message}
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
        >
          <FiX />
        </button>
      </div>
    </div>
  );
}

function ConfirmDialog({
  open,
  title,
  message,
  confirmText = "Confirm",
  danger = false,
  loading = false,
  onCancel,
  onConfirm,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-3xl border border-slate-100 bg-white p-6 shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div
          className={`grid h-14 w-14 place-items-center rounded-2xl ${
            danger
              ? "bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-300"
              : "bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-300"
          }`}
        >
          {danger ? (
            <FiTrash2 className="text-xl" />
          ) : (
            <FiInfo className="text-xl" />
          )}
        </div>

        <h3 className="mt-5 text-xl font-bold text-slate-950 dark:text-white">
          {title}
        </h3>

        <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
          {message}
        </p>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            disabled={loading}
            onClick={onCancel}
            className="rounded-xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Cancel
          </button>

          <button
            type="button"
            disabled={loading}
            onClick={onConfirm}
            className={`rounded-xl px-5 py-3 text-sm font-semibold text-white transition disabled:opacity-60 ${
              danger
                ? "bg-red-600 hover:bg-red-700"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {loading ? "Processing..." : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CostPriceList() {
  const [categories, setCategories] = useState([]);
  const [prices, setPrices] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingPrice, setEditingPrice] = useState(null);

  const [toast, setToast] = useState({ message: "", type: "success" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const showToast = (message, type = "success") => {
    setToast({ message, type });

    window.setTimeout(() => {
      setToast({ message: "", type: "success" });
    }, 2500);
  };

  const fetchData = async () => {
    const [catRes, priceRes] = await Promise.all([
      getCategories(),
      getCostPrices(),
    ]);

    setCategories(Array.isArray(catRes.data) ? catRes.data : []);
    setPrices(Array.isArray(priceRes.data) ? priceRes.data : []);
  };

  useEffect(() => {
    fetchData().catch((err) => {
      console.error(err);
      showToast(err.response?.data?.message || "Error loading freight rates.", "error");
    });
  }, []);

  const stats = useMemo(() => {
    const totalRates = prices.length;
    const activeRates = prices.filter((p) => p.status === "Active").length;

    const avgFreight = prices.length
      ? prices.reduce((sum, p) => sum + Number(p.cost || 0), 0) / prices.length
      : 0;

    const forwarders = new Set(
      prices.map((p) => (p.supplier || "").trim()).filter(Boolean)
    ).size;

    return { totalRates, activeRates, avgFreight, forwarders };
  }, [prices]);

  const filteredPrices = useMemo(() => {
    const q = search.toLowerCase().trim();

    return prices.filter((item) => {
      const matchesSearch =
        !q ||
        (item.productName || "").toLowerCase().includes(q) ||
        (item.supplier || "").toLowerCase().includes(q) ||
        (item.category?.name || "").toLowerCase().includes(q);

      const matchesCategory =
        !selectedCategory || Number(item.categoryId) === Number(selectedCategory);

      const matchesStatus = !selectedStatus || item.status === selectedStatus;

      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [prices, search, selectedCategory, selectedStatus]);

  const openAddCategory = () => {
    setEditingCategory(null);
    setShowCategoryModal(true);
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setShowCategoryModal(true);
  };

  const openAddPrice = () => {
    setEditingPrice(null);
    setShowPriceModal(true);
  };

  const openEditPrice = (price) => {
    setEditingPrice(price);
    setShowPriceModal(true);
  };

  const handleSaveCategory = async (payload) => {
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, payload);
        showToast("Route category updated successfully.");
      } else {
        await createCategory(payload);
        showToast("Route category created successfully.");
      }

      setEditingCategory(null);
      setShowCategoryModal(false);
      await fetchData();
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const handleSavePrice = async (payload) => {
    try {
      if (editingPrice) {
        await updateCostPrice(editingPrice.id, payload);
        showToast("Freight rate updated successfully.");
      } else {
        await createCostPrice(payload);
        showToast("Freight rate created successfully.");
      }

      setEditingPrice(null);
      setShowPriceModal(false);
      await fetchData();
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const requestDeleteCategory = (category) => {
    setDeleteTarget({ type: "category", item: category });
  };

  const requestDeletePrice = (price) => {
    setDeleteTarget({ type: "price", item: price });
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.item?.id) return;

    setDeleteLoading(true);

    try {
      if (deleteTarget.type === "category") {
        await deleteCategory(deleteTarget.item.id);
        showToast("Route category deleted successfully.");
      } else {
        await deleteCostPrice(deleteTarget.item.id);
        showToast("Freight rate deleted successfully.");
      }

      setDeleteTarget(null);
      await fetchData();
    } catch (err) {
      console.error(err);
      showToast(
        err.response?.data?.message || "Unable to delete this item.",
        "error"
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setSelectedCategory("");
    setSelectedStatus("");
  };

  const shell =
    "rounded-2xl border border-slate-200 bg-white shadow-sm transition-colors duration-300 dark:border-slate-800 dark:bg-slate-900";

  const input =
    "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none transition focus:border-transparent focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500";

  return (
    <div className="space-y-6 p-6 text-slate-900 dark:text-slate-100">
      <Toast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ message: "", type: "success" })}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={
          deleteTarget?.type === "category"
            ? "Delete route category?"
            : "Delete freight rate?"
        }
        message={
          deleteTarget?.type === "category"
            ? `Are you sure you want to delete "${
                deleteTarget?.item?.name || "this category"
              }"? If this category is used in a freight rate, it cannot be deleted.`
            : `Are you sure you want to delete "${
                deleteTarget?.item?.productName || "this freight rate"
              }"? This action cannot be undone.`
        }
        confirmText={
          deleteTarget?.type === "category" ? "Delete Category" : "Delete Rate"
        }
        danger
        loading={deleteLoading}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
            Freight Rates
          </h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Manage China to UK container freight and shipping cost rates.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={openAddCategory}
            className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <FiLayers />
            Add Route Category
          </button>

          <button
            type="button"
            onClick={openAddPrice}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-white transition hover:bg-blue-700"
          >
            <FiPlus />
            Add Freight Rate
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className={`${shell} p-4`}>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Total Rates
          </p>
          <div className="mt-2 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              {stats.totalRates}
            </h2>
            <FiTruck className="text-xl text-blue-600" />
          </div>
        </div>

        <div className={`${shell} p-4`}>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Active Rates
          </p>
          <div className="mt-2 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              {stats.activeRates}
            </h2>
            <FiTrendingUp className="text-xl text-green-600" />
          </div>
        </div>

        <div className={`${shell} p-4`}>
  <p className="text-sm text-slate-500 dark:text-slate-400">
    Avg Ocean Freight
  </p>
  <div className="mt-2 flex items-center justify-between">
    <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
      £{stats.avgFreight.toFixed(2)}
    </h2>
    <FiAnchor className="text-xl text-cyan-600" />
  </div>
</div>

        <div className={`${shell} p-4`}>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Carriers / Forwarders
          </p>
          <div className="mt-2 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              {stats.forwarders}
            </h2>
            <FiLayers className="text-xl text-purple-600" />
          </div>
        </div>
      </div>

      <div className={`${shell} p-5`}>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Route Categories
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          These categories appear in the freight rate form dropdown.
        </p>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          {categories.map((cat) => {
            const count = prices.filter(
              (p) => Number(p.categoryId) === Number(cat.id)
            ).length;

            return (
              <div
                key={cat.id}
                className="rounded-xl border border-slate-200 bg-white p-4 transition-colors duration-300 dark:border-slate-800 dark:bg-slate-950"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                      {cat.name}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {cat.description || "No description"}
                    </p>
                  </div>

                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    {count} rates
                  </span>
                </div>

                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={() => openEditCategory(cat)}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    Edit
                  </button>

                  <button
                    type="button"
                    onClick={() => requestDeleteCategory(cat)}
                    className="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-600 transition hover:bg-red-50 dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-950/40"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}

          {categories.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-slate-500 dark:border-slate-700 dark:text-slate-400">
              No route categories yet. Add your first category.
            </div>
          ) : null}
        </div>
      </div>

      <div className={`${shell} p-4`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Search route, carrier, forwarder or category..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-10 pr-4 text-slate-900 outline-none transition focus:border-transparent focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500"
            />
          </div>

          <button
            type="button"
            onClick={clearFilters}
            className="flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <FiFilter />
            Clear Filters
          </button>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 border-t border-slate-200 pt-4 dark:border-slate-800 md:grid-cols-3">
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Route Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className={input}
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Status
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className={input}
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              <tr>
                <th className="p-4 text-left">Route / Container Detail</th>
                <th className="p-4 text-left">Route Category</th>
                <th className="p-4 text-left">Carrier / Forwarder</th>
                <th className="p-4 text-left">Ocean Freight</th>
                <th className="p-4 text-left">UK / Delivery Charges</th>
                <th className="p-4 text-left">Total Cost</th>
                <th className="p-4 text-left">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {filteredPrices.map((item) => (
                <tr
                  key={item.id}
                  className="bg-white transition-colors hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800/60"
                >
                  <td className="p-4 font-medium text-slate-900 dark:text-slate-100">
                    {item.productName}
                  </td>

                  <td className="p-4 text-slate-500 dark:text-slate-400">
                    {item.category?.name || "N/A"}
                  </td>

                  <td className="p-4 text-slate-500 dark:text-slate-400">
                    {item.supplier}
                  </td>

                  <td className="p-4 text-slate-900 dark:text-slate-100">
                    £{Number(item.cost || 0).toFixed(2)}
                  </td>

                  <td className="p-4 text-slate-900 dark:text-slate-100">
                    £{Number(item.shipping || 0).toFixed(2)}
                  </td>

                  <td className="p-4 font-semibold text-slate-900 dark:text-slate-100">
                    £{Number(item.total || 0).toFixed(2)}
                  </td>

                  <td className="p-4">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-medium ${
                        item.status === "Active"
                          ? "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300"
                          : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                      }`}
                    >
                      {item.status}
                    </span>
                  </td>

                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openEditPrice(item)}
                        className="rounded-lg border border-slate-300 p-2 text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                        title="Edit rate"
                      >
                        <FiEdit />
                      </button>

                      <button
                        type="button"
                        onClick={() => requestDeletePrice(item)}
                        className="rounded-lg border border-red-200 p-2 text-red-600 transition hover:bg-red-50 dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-950/40"
                        title="Delete rate"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredPrices.length === 0 ? (
          <div className="py-12 text-center text-slate-500 dark:text-slate-400">
            No freight rates found
          </div>
        ) : null}

        <div className="flex items-center justify-between border-t border-slate-200 p-4 text-sm text-slate-600 dark:border-slate-800 dark:text-slate-400">
          <span>Total Rates: {filteredPrices.length}</span>
          <span>Total Route Categories: {categories.length}</span>
        </div>
      </div>

      <AddCategoryModal
        isOpen={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false);
          setEditingCategory(null);
        }}
        onSave={handleSaveCategory}
        editingCategory={editingCategory}
      />

      <AddCostPriceModal
        isOpen={showPriceModal}
        onClose={() => {
          setShowPriceModal(false);
          setEditingPrice(null);
        }}
        onSave={handleSavePrice}
        editingPrice={editingPrice}
        categories={categories}
      />
    </div>
  );
}